'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CreditCard,
  Lock,
  MapPin,
  PackageCheck,
  Plus,
  ShieldCheck,
  Truck,
  Check,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ClipboardCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCart } from '@/providers/CartProvider';
import { useStore } from '@/context/StoreContext';
import { useAuth } from '@/providers/AuthProvider';
import { OrdersApi } from '@/lib/Apis/apis/orders-api';
import { ShippingApi } from '@/lib/Apis/apis/shipping-api';
import { ProductVariantPopulatedInventoryStatusEnum, GuestAddressDto } from '@/lib/Apis/models';
import { useAddresses } from '@/hooks/useAddresses';
import { useStoreCapabilities } from '@/hooks/useStoreCapabilities';
import { formatPrice, formatPhoneNumber } from '@/lib/utils/format';
import { calculateDiscountForCartItems, isProductEligibleForDiscount } from '@/lib/utils/discount';
import { useRouter } from 'next/navigation';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { Card } from '@/components/ui/Card';
import { AddressFormModal } from '@/components/AddressFormModal';
import { Edit3, Trash2, MoreVertical, Star, Loader2 } from 'lucide-react';
import { useBasePath } from '@/providers/BasePathProvider';
import { CheckoutStepper, CheckoutStep } from '@/components/ui/CheckoutStepper';

import { addressSchema } from '@/lib/validations/address';
import { US_STATES } from '@/lib/constants/us-states';
import Image from 'next/image';
import { useNotification } from '@/providers/NotificationProvider';
import { CouponCodeInput } from '@/components/CouponCodeInput';
import { useDiscounts } from '@/hooks/useDiscounts';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { Modal } from '@/components/ui/Modal';
import { useProduct } from '@/hooks/useProducts';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';

function CheckoutItem({ item, isDiscounted }: { item: any; isDiscounted?: boolean }) {
  // Fallback Logic:
  // If the backend doesn't populate 'product' on the cart item, we can try to find a productId
  // and fetch the product details to get the media.
  const productId =
    item.productId ||
    item.product?._id ||
    item.product?.id ||
    item.productVariantData?.productId ||
    item.productVariantData?.product?._id ||
    item.productVariantData?.product?.id;

  const { product } = useProduct(productId);

  return (
    <div className="flex gap-3">
      <div className="w-16 h-16 rounded-xl overflow-hidden bg-white shrink-0">
        <Image
          src={item.productVariantData?.media?.[0]?.file || item.product?.media?.[0]?.file || product?.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
          alt={item.productVariantData?.name}
          className="w-full h-full object-cover"
          height={200}
          width={200}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[12px] text-[#2B4B7C] mb-0.5">
          {item?.product?.name || product?.name || item?.productVariantData?.name}
        </p>
        {(item?.product?.name || product?.name) && (
          <p className="text-[11px] text-[#676c80] mb-0.5">
            {item?.productVariantData?.name}
          </p>
        )}
        <p className="text-[11px] text-[#676c80]">
          {item?.productVariantData?.brand} • Qty: {item.quantity}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <p className="font-semibold text-[12px] text-hsecondary">
            {formatPrice(item.productVariantData.finalPrice * item.quantity)}
          </p>
          {isDiscounted && (
            <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 leading-none">
              Discounted
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const relaxedAddressSchema = z.object({
  name: z.string().optional(),
  street1: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
});

const checkoutSchema = z.object({
  shipping: relaxedAddressSchema,
  billing: relaxedAddressSchema,
  sameAsShipping: z.boolean().default(true),
});

type CheckoutFormData = z.infer<typeof checkoutSchema>;



const PAYMENT_METHODS_CONFIG = [
  {
    id: 'Card',
    label: 'Card',
    value: 'Card',
    icon: <CreditCard className="w-5 h-5" />,
    description: 'Pay securely with your credit or debit card',
    className: 'border-blue-500 hover:bg-blue-50',
    activeClass: 'bg-blue-50 border-blue-500 text-blue-700',
    requiresStripe: true,
  },
  {
    id: 'Cash',
    label: 'Cash',
    value: 'Cash',
    icon: <PackageCheck className="w-5 h-5" />,
    description: 'Pay with cash on delivery or at pickup',
    className: 'border-amber-500 hover:bg-amber-50',
    activeClass: 'bg-amber-50 border-amber-500 text-amber-700',
    requiresStripe: false,
  },
];

const SHIPPO_DELIVERY_NAME = 'Shipping Delivery';
const SHIPPING_ALTERNATIVE_MESSAGE = 'Please use Shipping instead of Local Delivery to continue checkout.';

interface CardSectionHandle {
  createPaymentMethod: () => Promise<{ id: string } | { error: string }>;
}

const CardSection = forwardRef<CardSectionHandle, {
  onCardChange: (complete: boolean, errorMessage?: string) => void;
}>(function CardSection({ onCardChange }, ref) {
  const stripe = useStripe();
  const elements = useElements();

  const [numberComplete, setNumberComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  const [cardErrorMessage, setCardErrorMessage] = useState<string | undefined>(undefined);

  // Report combined completeness from a single effect rather than inline in each
  // element's onChange. Browser autofill fills all three Stripe fields and fires
  // their onChange events before React re-renders, so an inline handler reading
  // the sibling fields' state would see stale `false` values and never mark the
  // card complete — leaving "Preview order" greyed out until the user manually
  // retypes a field. Deriving from state here always sees the latest values.
  useEffect(() => {
    onCardChange(numberComplete && expiryComplete && cvcComplete, cardErrorMessage);
  }, [numberComplete, expiryComplete, cvcComplete, cardErrorMessage, onCardChange]);

  const elementStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#2B4B7C',
        fontFamily: 'inherit',
        '::placeholder': { color: '#9ca3af' },
      },
      invalid: { color: '#ef4444' },
    },
  };

  useImperativeHandle(ref, () => ({
    createPaymentMethod: async () => {
      if (!stripe || !elements) {
        return { error: 'Payment processing is not ready. Please try again.' };
      }
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        return { error: 'Card element not found. Please try again.' };
      }
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
      });
      if (error) {
        return { error: error.message || 'Card validation failed.' };
      }
      return { id: paymentMethod!.id };
    },
  }));

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-600">Card Number</label>
        <div className="border border-gray-200 rounded-xl px-4 py-3.5 bg-white focus-within:border-blue-400 transition-colors">
          <CardNumberElement
            options={{ ...elementStyle, showIcon: true }}
            onChange={(e) => {
              setNumberComplete(e.complete);
              setCardErrorMessage(e.error?.message);
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-600">Expiry Date</label>
          <div className="border border-gray-200 rounded-xl px-4 py-3.5 bg-white focus-within:border-blue-400 transition-colors">
            <CardExpiryElement
              options={elementStyle}
              onChange={(e) => {
                setExpiryComplete(e.complete);
                setCardErrorMessage(e.error?.message);
              }}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-600">CVC</label>
          <div className="border border-gray-200 rounded-xl px-4 py-3.5 bg-white focus-within:border-blue-400 transition-colors">
            <CardCvcElement
              options={elementStyle}
              onChange={(e) => {
                setCvcComplete(e.complete);
                setCardErrorMessage(e.error?.message);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

interface GuestInfo {
  firstname: string;
  lastname: string;
  email: string;
}

interface GuestAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  name: string;
  phone: string;
}

export function CheckoutScreen() {
  const router = useRouter();
  const { cart, clearCart, updateQuantity, removeFromCart, refreshCart, isLoading: cartIsLoading, isFetching: cartIsFetching } = useCart();
  const { setCartOpen } = useStore();
  const { isAuthenticated, user, loginWithSession } = useAuth();
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({ firstname: '', lastname: '', email: '' });
  const [guestDeliveryAddress, setGuestDeliveryAddress] = useState<GuestAddress>({ street1: '', city: '', state: '', zip: '', country: 'US', name: '', phone: '' });
  const [guestInfoError, setGuestInfoError] = useState<string | null>(null);
  const [guestAddressError, setGuestAddressError] = useState<string | null>(null);
  const [guestAddressValidated, setGuestAddressValidated] = useState(false);
  const [customDeliveryAddressError, setCustomDeliveryAddressError] = useState<string | null>(null);
  const [isCustomDeliveryValidating, setIsCustomDeliveryValidating] = useState(false);
  const [guestAddressValidating, setGuestAddressValidating] = useState(false);
  const { buildPath } = useBasePath();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDelivery, setIsDelivery] = useState<boolean | 'custom' | null>(null); // Start with no selection
  const [hasSetDefaultDelivery, setHasSetDefaultDelivery] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null); // null until capabilities load
  const [error, setError] = useState<string | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [storeAddresses, setStoreAddresses] = useState<any[]>([]); // For pickup selection
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
  const [shippingPrice, setShippingPrice] = useState(0);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hasInitializedAddressRef = useRef(false);
  const notification = useNotification();
  const { appliedCoupon, removeCoupon } = useDiscounts();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [currentStep, setCurrentStep] = useState<CheckoutStep>('shipping');
  const [completedSteps, setCompletedSteps] = useState<CheckoutStep[]>([]);
  const [isOrderSuccess, setIsOrderSuccess] = useState(false);
  const [hasAttemptedShippingContinue, setHasAttemptedShippingContinue] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [quantityAdjustItems, setQuantityAdjustItems] = useState<any[]>([]);
  const [activeShippingSection, setActiveShippingSection] = useState<'method' | 'address' | 'rate' | null>('method');

  // Stripe card payment state
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [storedPaymentMethodId, setStoredPaymentMethodId] = useState<string | null>(null);
  const [isCreatingPaymentMethod, setIsCreatingPaymentMethod] = useState(false);
  const cardSectionRef = useRef<CardSectionHandle>(null);



  // Get store capabilities for payment/delivery options
  const {
    capabilities,
    isLoading: capabilitiesLoading,
    showCardPayment,
    showCashPayment,
    showShippoDelivery,
    showCustomDelivery,
  } = useStoreCapabilities();

  // Load Stripe with the connected account's publishable key (only when card payment is enabled)
  const stripePromise = useMemo(() => {
    const key = capabilities?.stripePublishableKey;
    if (key && showCardPayment) return loadStripe(key);
    return null;
  }, [capabilities?.stripePublishableKey, showCardPayment]);

  // Filter payment methods based on capabilities and the selected delivery method.
  // Shippo orders must be prepaid, so cash is never offered for Shipping Delivery.
  const availablePaymentMethods = useMemo(() => PAYMENT_METHODS_CONFIG.filter(pm => {
    if (pm.requiresStripe) {
      return showCardPayment;
    }
    return showCashPayment && isDelivery !== true;
  }), [showCardPayment, showCashPayment, isDelivery]);

  // Set default payment method once capabilities are loaded
  useEffect(() => {
    if (capabilitiesLoading) return;

    const selectedMethodIsAvailable = availablePaymentMethods.some(pm => pm.value === paymentMethod);
    if (availablePaymentMethods.length === 0) {
      if (paymentMethod !== null) setPaymentMethod(null);
      return;
    }
    if (paymentMethod === null || !selectedMethodIsAvailable) {
      // Prefer Card if available, otherwise use first available.
      const preferCard = availablePaymentMethods.find(pm => pm.id === 'Card');
      setPaymentMethod(preferCard ? 'Card' : availablePaymentMethods[0].value);
    }
  }, [capabilitiesLoading, paymentMethod, availablePaymentMethods]);

  // Set default delivery method once capabilities are loaded
  useEffect(() => {
    if (!capabilitiesLoading && !hasSetDefaultDelivery && capabilities) {
      // Only default to Pickup if NO delivery options are available
      if (!showShippoDelivery && !showCustomDelivery) {
        setIsDelivery(false);
      }
      // Otherwise leave as null (User must choose)
      setHasSetDefaultDelivery(true);
    }
  }, [capabilitiesLoading, hasSetDefaultDelivery, capabilities, showShippoDelivery, showCustomDelivery]);

  // Build delivery options based on capabilities
  const deliveryOptions = [
    // Shippo delivery (if enabled)
    ...(showShippoDelivery ? [{
      id: 'shippo',
      label: 'Delivery',
      icon: <Truck className="w-5 h-5" />,
      value: true as const,
      desc: SHIPPO_DELIVERY_NAME,
    }] : []),
    // Custom/Store delivery (if enabled)
    ...(showCustomDelivery ? [{
      id: 'custom',
      label: capabilities?.customDeliveryTitle || 'Store Delivery',
      icon: <Truck className="w-5 h-5" />,
      value: 'custom' as const,
      desc: 'Price based on distance',
    }] : []),
    {
      id: 'pickup',
      label: 'Pickup',
      icon: <MapPin className="w-5 h-5" />,
      value: false as const,
      desc: 'Collect from pharmacy',
    },
  ];
  const [selectedShippingRateId, setSelectedShippingRateId] = useState<string | null>(null);
  const [shippingRates, setShippingRates] = useState<any[]>([]);
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [shippingRatesError, setShippingRatesError] = useState<string | null>(null);
  const [storeData, setStoreData] = useState<any>(null); // Simulate store data if needed
  const { addresses: userAddresses, defaultAddress, isLoading, refresh, removeAddress, markAsDefault } = useAddresses();

  const handleAddressSelect = (addr: any) => {
    setSelectedAddressId(addr.id);
    if (addr.id !== selectedAddressId) {
      setCustomDeliveryAddressError(null);
    }
    setHasAttemptedShippingContinue(false);
    setActiveShippingSection(isDelivery === true ? 'rate' : null);
    setValue('shipping.name', addr.name);
    setValue('shipping.phone', addr.phone || '');
    setValue('shipping.street1', addr.street1);
    setValue('shipping.street2', addr.street2 || '');
    setValue('shipping.city', addr.city);
    setValue('shipping.state', addr.state);
    setValue('shipping.zip', addr.zip);
    setValue('shipping.country', addr.country);
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      sameAsShipping: true,
      shipping: {
        name: user ? `${user.firstname} ${user.lastname}` : '',
        phone: user?.phoneNumber || '',
        country: 'United States',
        street1: '',
        city: '',
        state: '',
        zip: '',
      },
      billing: {
        name: user ? `${user.firstname} ${user.lastname}` : '',
        phone: user?.phoneNumber || '',
        country: 'United States',
        street1: '',
        city: '',
        state: '',
        zip: '',
      },
    },
  });

  const sameAsShipping = watch('sameAsShipping', true);

  const stockIssues = useMemo(() => {
    if (!cart?.cartBody?.items) return [];
    return cart.cartBody.items.filter(item => {
      const v = item.productVariantData;
      if (!v) return false;
      if (v.inventoryStatus === ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK) return true;
      if (typeof v.inventoryCount === 'number' && v.inventoryCount < item.quantity) return true;
      return false;
    });
  }, [cart]);

  const canProceedToPayment = useMemo(() => {
    if (stockIssues.length > 0) return false;
    if (isDelivery === null) return false;
    if (!isAuthenticated) {
      // Guest info must be complete before any other shipping checks
      if (!guestInfo.firstname.trim() || !guestInfo.lastname.trim() || !guestInfo.email.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestInfo.email)) return false;
    }
    const needsDeliveryAddress = isDelivery === true || isDelivery === 'custom';
    if (needsDeliveryAddress) {
      if (isDelivery === 'custom' && customDeliveryAddressError) return false;
      if (isAuthenticated) {
        if (!selectedAddressId) return false;
        if (isDelivery === 'custom' && isCustomDeliveryValidating) return false;
        if (isDelivery === true && !selectedShippingRateId) return false;
      } else {
        // Guest: needs inline address fields and a validated address
        if (!guestDeliveryAddress.street1 || !guestDeliveryAddress.city || !guestDeliveryAddress.state || !guestDeliveryAddress.zip || !guestDeliveryAddress.name || !guestDeliveryAddress.phone) return false;
        if (!guestAddressValidated) return false;
        // Guest + Shippo: also needs a rate selected
        if (isDelivery === true && !selectedShippingRateId) return false;
      }
    }
    return true;
  }, [stockIssues, isDelivery, selectedAddressId, selectedShippingRateId, isAuthenticated, guestDeliveryAddress, guestInfo, guestAddressValidated, customDeliveryAddressError, isCustomDeliveryValidating]);

  const canProceedToReview = useMemo(() => {
    if (!paymentMethod) return false;
    if (paymentMethod === 'Card' && stripePromise && !cardComplete) return false;
    return true;
  }, [paymentMethod, cardComplete, stripePromise]);

  // Reduce shipping rates to one best-value option per speed tier
  const tieredShippingRates = useMemo(() => {
    if (!shippingRates.length) return [];

    const TIERS: Record<string, { label: string; bg: string; text: string }> = {
      economy: { label: 'Economy', bg: 'bg-gray-100', text: 'text-gray-600' },
      standard: { label: 'Standard', bg: 'bg-gray-100', text: 'text-gray-600' },
      express: { label: 'Express', bg: 'bg-gray-100', text: 'text-gray-600' },
      overnight: { label: 'Overnight', bg: 'bg-gray-100', text: 'text-gray-600' },
    };

    const getTierKey = (days?: number) => {
      if (!days || days >= 5) return 'economy';
      if (days >= 3) return 'standard';
      if (days === 2) return 'express';
      return 'overnight';
    };

    // Rates are already sorted cheapest-first; keep first (cheapest) per tier
    const seenTiers = new Set<string>();
    return shippingRates
      .map(rate => ({ ...rate, tierKey: getTierKey(rate.estimatedDays) }))
      .filter(rate => {
        if (seenTiers.has(rate.tierKey)) return false;
        seenTiers.add(rate.tierKey);
        return true;
      })
      .map(rate => ({ ...rate, tierConfig: TIERS[rate.tierKey] }));
  }, [shippingRates]);

  const handleStepClick = (step: CheckoutStep) => {
    const stepOrder: CheckoutStep[] = ['shipping', 'payment', 'review'];
    const currentIndex = stepOrder.indexOf(currentStep);
    const targetIndex = stepOrder.indexOf(step);

    if (targetIndex <= currentIndex || completedSteps.includes(step)) {
      setCurrentStep(step);
      // Scroll to the step section
      setTimeout(() => {
        const ref = step === 'shipping' ? shippingRef : step === 'payment' ? paymentRef : reviewRef;
        if (ref.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollBy({ top: -900, left: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const goToNextStep = () => {
    const stepOrder: CheckoutStep[] = ['shipping', 'payment', 'review'];
    const currentIndex = stepOrder.indexOf(currentStep);

    if (currentStep === 'shipping') {
      setCompletedSteps(prev => [...prev.filter(s => s !== 'shipping'), 'shipping']);
    }
    if (currentStep === 'payment') {
      setCompletedSteps(prev => [...prev.filter(s => s !== 'payment'), 'payment']);
    }

    if (currentIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[currentIndex + 1];
      setCurrentStep(nextStep);
      // Scroll to the step section
      setTimeout(() => {
        const ref = nextStep === 'shipping' ? shippingRef : nextStep === 'payment' ? paymentRef : reviewRef;
        if (ref.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollBy({ top: -900, left: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Refs for each step to enable scrolling into view on navigation
  const shippingRef = useRef<HTMLDivElement | null>(null);
  const paymentRef = useRef<HTMLDivElement | null>(null);
  const reviewRef = useRef<HTMLDivElement | null>(null);

  // Scroll to active step when currentStep changes
  useEffect(() => {
    const scrollTo = (el: HTMLElement | null) => {
      if (!el) return;
      // use a small timeout so the element is visible after animation/layout
      setTimeout(() => {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // compensate a little for sticky headers
          window.scrollBy({ top: -200, left: 0, behavior: 'smooth' });
        } catch (e) {
          // fallback
          const rect = el.getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + rect.top - 12, behavior: 'smooth' });
        }
      }, 80);
    };

    if (currentStep === 'shipping') scrollTo(shippingRef.current);
    if (currentStep === 'payment') scrollTo(paymentRef.current);
    if (currentStep === 'review') scrollTo(reviewRef.current);
  }, [currentStep]);

  const goToPreviousStep = () => {
    const stepOrder: CheckoutStep[] = ['shipping', 'payment', 'review'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStep = stepOrder[currentIndex - 1];
      setCurrentStep(prevStep);
      // Scroll to the step section
      setTimeout(() => {
        const ref = prevStep === 'shipping' ? shippingRef : prevStep === 'payment' ? paymentRef : reviewRef;
        if (ref.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollBy({ top: -80, left: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Sync billing address with shipping address when 'Billing same as shipping' is checked
  useEffect(() => {
    if (sameAsShipping) {
      setValue('billing.name', watch('shipping.name'));
      setValue('billing.phone', watch('shipping.phone'));
      setValue('billing.street1', watch('shipping.street1'));
      setValue('billing.street2', watch('shipping.street2'));
      setValue('billing.city', watch('shipping.city'));
      setValue('billing.state', watch('shipping.state'));
      setValue('billing.zip', watch('shipping.zip'));
      setValue('billing.country', watch('shipping.country'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsShipping, watch('shipping.name'), watch('shipping.phone'), watch('shipping.street1'), watch('shipping.street2'), watch('shipping.city'), watch('shipping.state'), watch('shipping.zip'), watch('shipping.country')]);

  // const handleAddressAdded = async (address: any) => {
  //   await refresh();
  // };

  useEffect(() => {
    if (defaultAddress && !selectedAddressId && !hasInitializedAddressRef.current) {
      hasInitializedAddressRef.current = true;
      setSelectedAddressId(defaultAddress.id);
      // Update form with default address
      setValue('shipping.name', defaultAddress.name);
      setValue('shipping.phone', defaultAddress.phone || '');
      setValue('shipping.street1', defaultAddress.street1);
      setValue('shipping.street2', defaultAddress.street2 || '');
      setValue('shipping.city', defaultAddress.city);
      setValue('shipping.state', defaultAddress.state);
      setValue('shipping.zip', defaultAddress.zip);
      setValue('shipping.country', defaultAddress.country);
    }
  }, [defaultAddress, selectedAddressId, setValue]);

  // Parse raw Shippo/backend error strings into a human-readable message
  const parseShippingError = (raw: string): string => {
    try {
      const bodyMatch = raw.match(/Body\s*(\{[\s\S]*\})/);
      if (bodyMatch) {
        const body = JSON.parse(bodyMatch[1]);
        const field = body.address_to ?? body.address_from ?? body;
        const errors: string[] = Array.isArray(field)
          ? field.flatMap((e: any) =>
            Object.values(e).flat().filter((v): v is string => typeof v === 'string')
          )
          : Object.values(field).flat().filter((v): v is string => typeof v === 'string');
        if (errors.length > 0) {
          return `${errors[0]}. Please edit your delivery address and try again.`;
        }
      }
    } catch {
      // ignore parse errors
    }
    return 'Shipping is not available to this address. Please verify the address details and try again.';
  };

  const getCustomDeliveryErrorMessage = (error: any): string => {
    const raw = error?.response?.data?.message || error?.message || '';
    const message = raw || 'Local Delivery is not available for this address.';
    if (!showShippoDelivery || /use shipping/i.test(message)) return message;
    return `${message} ${SHIPPING_ALTERNATIVE_MESSAGE}`;
  };

  const handleUseShipping = () => {
    if (!showShippoDelivery) return;

    setIsDelivery(true);
    setHasAttemptedShippingContinue(false);
    setError(null);
    setCustomDeliveryAddressError(null);
    setGuestAddressError(null);
    setGuestAddressValidated(false);
    setShippingRatesError(null);
    setShippingRates([]);
    setSelectedShippingRateId(null);
    setShippingPrice(0);
    setActiveShippingSection(isAuthenticated && selectedAddressId ? 'rate' : 'address');
    setCompletedSteps(prev => prev.filter(step => step !== 'shipping'));
    setCurrentStep('shipping');
  };

  // Handle shipping rates response and errors
  const handleShippingResponse = (response: any) => {
    if (!response?.data) {
      setShippingRatesError('Unable to calculate shipping rates');
      setShippingRates([]);
      setSelectedShippingRateId(null);
      return;
    }

    const rates = response.data.rates || [];
    if (rates.length > 0) {
      // Sort rates by price (cheapest first) and select the first one
      const sortedRates = [...rates].sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
      const cheapestRate = sortedRates[0];

      setShippingRates(sortedRates);
      setShippingRatesError(null);
      setSelectedShippingRateId(cheapestRate.objectId);
      setShippingPrice(parseFloat(cheapestRate.amount));
      setActiveShippingSection(null);
      return;
    }

    // If no rates, show the most relevant error message from messages
    const messages = response.data.messages || [];
    // Prefer messages that mention 'shipping', 'service area', or are from USPS
    const relevantMessage = messages.find((msg: any) =>
      msg.text.toLowerCase().includes('shipping') ||
      msg.text.toLowerCase().includes('service area') ||
      msg.source === 'USPS'
    ) || messages[0];
    setShippingRatesError(relevantMessage?.text || 'Shipping is not available to this address');
    setShippingRates([]);
    setSelectedShippingRateId(null);
  };

  // Define shipping message types
  interface ShippingMessage {
    source: string;
    code: string;
    text: string;
  }

  interface ShippingRate {
    objectId: string;
    provider: string;
    servicelevel: {
      name: string;
      token: string;
    };
    amount: string;
    amountLocal: string;
    currency: string;
    currencyLocal: string;
    durationTerms?: string;
    estimatedDays?: number;
    providerImage75?: string;
    test?: boolean;
  }

  useEffect(() => {
    if (!isAuthenticated || isDelivery !== true || !selectedAddressId || !cart || cart?.cartBody?.items?.length === 0 || !cart?.cartBody?.items) {
      setShippingRates([]);
      setSelectedShippingRateId(null);
      return;
    }
    setShippingRatesLoading(true);
    setShippingRatesError(null);
    (async () => {
      try {
        const api = new ShippingApi(AXIOS_CONFIG);
        const cartBody = {
          items: cart?.cartBody?.items?.map((item: any) => ({
            productVariantId: String(item.productVariantId),
            quantity: typeof item.quantity === 'number' ? item.quantity : 1,
          })),
          discount: undefined,
        };
        const res = await api.fetchAvailableShippingRates(cartBody, selectedAddressId);
        handleShippingResponse(res);
      } catch (e: any) {
        setShippingRates([]);
        setSelectedShippingRateId(null);
        setShippingRatesError(parseShippingError(e?.response?.data?.message || e?.message || ''));
      } finally {
        setShippingRatesLoading(false);
      }
    })();
  }, [isDelivery, selectedAddressId]);

  // Clear previously fetched guest rates and validation whenever the address changes
  const clearGuestRates = () => {
    setShippingRates([]);
    setSelectedShippingRateId(null);
    setShippingRatesError(null);
    setGuestAddressValidated(false);
  };

  // Validate address then proceed: for Shippo → also fetch rates; for custom → mark as validated
  const validateAndProceed = async () => {
    const { street1, street2, city, state, zip, country, name, phone } = guestDeliveryAddress;
    if (!street1.trim() || !city.trim() || !state.trim() || !zip.trim() || !name?.trim() || !phone?.trim()) {
      setGuestAddressError('Please fill in all required address fields.');
      return;
    }
    setGuestAddressValidating(true);
    setGuestAddressError(null);
    setCustomDeliveryAddressError(null);
    setGuestAddressValidated(false);
    const trimmedAddress: GuestAddressDto = {
      street1: street1.trim(),
      street2: street2?.trim() || undefined,
      city: city.trim(),
      state: state.trim().toUpperCase(),
      zip: zip.trim(),
      country: country.trim().toUpperCase() || 'US',
      name: name.trim() || undefined,
      phone: phone.replace(/[\s-]/g, '').trim() || undefined,
    };
    try {
      const api = new ShippingApi(AXIOS_CONFIG);
      await api.validateGuestAddress(trimmedAddress);
      if (isDelivery === true) {
        // Shippo: chain into rate fetch immediately
        setGuestAddressValidated(true);
        setGuestAddressValidating(false);
        await fetchGuestRates();
      } else if (isDelivery === 'custom') {
        // Custom: refresh with the validated address so the backend can apply zone-based pricing
        setGuestAddressValidating(false);
        setIsCustomDeliveryValidating(true);
        const discountId = appliedCoupon?._id || appliedCoupon?.id || undefined;
        try {
          await refreshCart('custom', undefined, discountId, undefined, trimmedAddress);
          setGuestAddressValidated(true);
          setCustomDeliveryAddressError(null);
        } catch (err: any) {
          const msg = getCustomDeliveryErrorMessage(err);
          setGuestAddressError(msg);
          setCustomDeliveryAddressError(msg);
          setGuestAddressValidated(false);
        } finally {
          setIsCustomDeliveryValidating(false);
        }
      }
    } catch (e: any) {
      const msg: string = e?.response?.data?.message || 'We couldn\'t verify this address. Please double-check your street, city, and ZIP code.';
      setGuestAddressError(msg);
      setGuestAddressValidated(false);
    } finally {
      setGuestAddressValidating(false);
    }
  };

  // Fetch Shippo rates for a guest using the inline address.
  // Returns the newly selected rateId on success, null on failure.
  const fetchGuestRates = async (): Promise<string | null> => {
    if (!cart?.cartBody?.items?.length) return null;
    const { street1, city, state, zip, country, street2, name, phone } = guestDeliveryAddress;
    if (!street1.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setGuestAddressError('Please fill in all required address fields before getting shipping rates.');
      return null;
    }
    setShippingRatesLoading(true);
    setShippingRatesError(null);
    setGuestAddressError(null);
    setShippingRates([]);
    setSelectedShippingRateId(null);
    try {
      const api = new ShippingApi(AXIOS_CONFIG);
      const res = await api.fetchGuestShippingRates({
        name: name?.trim() || '',
        address: {
          street1: street1.trim(),
          street2: street2?.trim() || undefined,
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
          country: country.trim().toUpperCase() || 'US',
          phone: phone?.replace(/[\s-]/g, '').trim() || undefined,
        },
        cart: {
          items: cart.cartBody.items.map((item: any) => ({
            productVariantId: String(item.productVariantId),
            quantity: typeof item.quantity === 'number' ? item.quantity : 1,
          })),
        },
      } as any);
      // Extract cheapest rate directly (same logic as handleShippingResponse)
      const rates: any[] = (res?.data as any)?.rates || [];
      if (rates.length > 0) {
        const sorted = [...rates].sort((a: any, b: any) => parseFloat(a.amount) - parseFloat(b.amount));
        const cheapest = sorted[0];
        handleShippingResponse(res);
        return cheapest.objectId as string;
      }
      handleShippingResponse(res);
      return null;
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || '';
      setShippingRates([]);
      setSelectedShippingRateId(null);
      // Show address errors inline on the address form
      if (e?.response?.status === 400) {
        setGuestAddressError(msg || 'We couldn\'t verify this address. Please check the details and try again.');
      } else {
        setShippingRatesError(parseShippingError(msg));
      }
      return null;
    } finally {
      setShippingRatesLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    if (isDelivery === null) return;

    // Custom delivery: price depends on distance
    // Auth: handled by the selectedAddressId effect; Guest: handled by the guestAddressValidated effect
    if (isDelivery === 'custom') {
      return () => { cancelled = true; };
    }

    const deliveryMethod = isDelivery === true ? 'shippo' : 'pickup';
    refreshCart(deliveryMethod).catch(() => {});

    if (isDelivery === false) {
      (async () => {
        const api = new ShippingApi(AXIOS_CONFIG);
        const res = await api.getStoreAddress();
        // Only update state if this effect hasn't been cleaned up
        if (!cancelled && res.data) {
          setStoreAddresses([res.data]);
          setShippingPrice(0);
        }
      })();
    }

    // Cleanup function to prevent stale updates when delivery method changes
    return () => {
      cancelled = true;
    };
  }, [isDelivery]);

  // Auto-fetch guest rates when switching to Shippo delivery with an already-validated address
  useEffect(() => {
    if (isDelivery !== true || isAuthenticated || !guestAddressValidated) return;
    fetchGuestRates();
  }, [isDelivery]);

  // Refresh cart prices whenever the selected Shippo rate changes
  useEffect(() => {
    if (isDelivery !== true || !selectedShippingRateId) return;
    refreshCart('shippo', selectedShippingRateId).catch(() => {});
  }, [selectedShippingRateId]);

  // Refresh cart for custom delivery whenever guest switches to custom with a validated address
  useEffect(() => {
    if (isDelivery !== 'custom' || isAuthenticated || !guestAddressValidated || !guestDeliveryAddress.street1?.trim()) return;
    setCustomDeliveryAddressError(null);
    setIsCustomDeliveryValidating(true);
    const discountId = appliedCoupon?._id || appliedCoupon?.id || undefined;
    refreshCart('custom', undefined, discountId, undefined, guestDeliveryAddress)
      .catch((err: any) => {
        const msg = getCustomDeliveryErrorMessage(err);
        setGuestAddressError(msg);
        setCustomDeliveryAddressError(msg);
        setGuestAddressValidated(false);
        setCurrentStep('shipping');
      })
      .finally(() => {
        setIsCustomDeliveryValidating(false);
      });
  }, [isDelivery, isAuthenticated, guestAddressValidated, guestDeliveryAddress, refreshCart]);

  // Refresh cart for custom delivery whenever auth user selects/changes address
  useEffect(() => {
    if (isDelivery !== 'custom' || !isAuthenticated || !selectedAddressId) return;
    setCustomDeliveryAddressError(null);
    setIsCustomDeliveryValidating(true);
    const discountId = appliedCoupon?._id || appliedCoupon?.id || undefined;
    refreshCart('custom', undefined, discountId, selectedAddressId)
      .catch((err: any) => {
        const statusCode = err?.response?.status || 0;
        if (statusCode === 400) {
          setCustomDeliveryAddressError(getCustomDeliveryErrorMessage(err));
          setCurrentStep('shipping');
          setActiveShippingSection('address');
        }
      })
      .finally(() => {
        setIsCustomDeliveryValidating(false);
      });
  }, [isDelivery, isAuthenticated, selectedAddressId, refreshCart]);

  // Re-fetch cart from backend with discount applied whenever coupon changes
  useEffect(() => {
    if (isDelivery === 'custom' && isAuthenticated && !selectedAddressId) return;
    if (isDelivery === 'custom' && !isAuthenticated && !guestAddressValidated) return;
    const deliveryMethod = isDelivery === null ? undefined : isDelivery === true ? 'shippo' : isDelivery === 'custom' ? 'custom' : 'pickup';
    const rateId = isDelivery === true ? (selectedShippingRateId ?? undefined) : undefined;
    const discountId = appliedCoupon?._id || appliedCoupon?.id || undefined;
    const addressId = (isDelivery === 'custom' && isAuthenticated) ? (selectedAddressId ?? undefined) : undefined;
    const guestAddr = (isDelivery === 'custom' && !isAuthenticated && guestAddressValidated) ? guestDeliveryAddress : undefined;
    refreshCart(deliveryMethod, rateId, discountId, addressId, guestAddr).catch((err: any) => {
      if (isDelivery !== 'custom') return;
      const msg = getCustomDeliveryErrorMessage(err);
      setCustomDeliveryAddressError(msg);
      if (!isAuthenticated) {
        setGuestAddressError(msg);
        setGuestAddressValidated(false);
      } else {
        setActiveShippingSection('address');
      }
      setCurrentStep('shipping');
    });
  }, [appliedCoupon, isDelivery, isAuthenticated, selectedAddressId, selectedShippingRateId, guestAddressValidated, guestDeliveryAddress, refreshCart]);


  const onSubmit = async (data: CheckoutFormData) => {
    setError(null);
    // Guest checkout: validate guest info
    if (!isAuthenticated) {
      if (!guestInfo.firstname.trim() || !guestInfo.lastname.trim() || !guestInfo.email.trim()) {
        setGuestInfoError('Please fill in your name and email above to continue.');
        setError('Please fill in your guest information above.');
        notification.error('Missing information', 'Please enter your name and email to continue as a guest.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestInfo.email)) {
        setGuestInfoError('Please enter a valid email address.');
        setError('Please enter a valid email address in the guest info section above.');
        return;
      }
    }
    if (!cart || cart?.cartBody?.items?.length === 0 || !cart?.cartBody?.items) {
      setError('Your cart is empty. Please add items to your cart.');
      return;
    }
    if (!paymentMethod) {
      setError('Please select a payment method.');
      return;
    }
    if (paymentMethod === 'Card' && !storedPaymentMethodId) {
      setError('Please enter your card details in the Payment step.');
      return;
    }
    if (isDelivery === null) {
      setError('Please select a delivery method.');
      return;
    }
    // Check for delivery-related requirements (both shippo and custom delivery need address)
    const isShippoDelivery = isDelivery === true;
    const isCustomDelivery = isDelivery === 'custom';
    const needsDeliveryAddress = isShippoDelivery || isCustomDelivery;

    if (needsDeliveryAddress) {
      if (isCustomDelivery && customDeliveryAddressError) {
        setError(customDeliveryAddressError);
        setCurrentStep('shipping');
        setActiveShippingSection('address');
        return;
      }
      if (!isAuthenticated) {
        // Guest: validate inline address
        if (!guestDeliveryAddress.street1 || !guestDeliveryAddress.city || !guestDeliveryAddress.state || !guestDeliveryAddress.zip) {
          setError('Please fill in your delivery address in the Shipping step.');
          return;
        }
        if (isCustomDelivery && !guestAddressValidated) {
          setError('Please confirm your delivery address before placing the order.');
          setCurrentStep('shipping');
          return;
        }
      } else {
        if (!selectedAddressId) {
          setError('Please select a shipping address.');
          return;
        }
        if (isShippoDelivery && !selectedShippingRateId) {
          setError('Please select a shipping method.');
          return;
        }
        const requiredFields = ['name', 'street1', 'city', 'zip', 'country'];
        for (const field of requiredFields) {
          if (!data.shipping[field as keyof typeof data.shipping]) {
            setError(`Please fill in the shipping address: ${field}.`);
            return;
          }
        }
      }
    } else if (isDelivery === false) {
      // Pickup case
      if (storeAddresses.length === 0) {
        setError('Store pickup location is not available.');
        return;
      }
      if (isAuthenticated && paymentMethod === 'Card' && userAddresses.length === 0) {
        setError('Card payments require a billing address. Please add an address to your account or choose a different payment method.');
        return;
      }
    }
    if (!sameAsShipping) {
      const requiredBillingFields = ['fullName', 'addressLine1', 'city', 'zipCode', 'country'];
      for (const field of requiredBillingFields) {
        if (!data.billing[field as keyof typeof data.billing]) {
          setError(`Please fill in the billing address: ${field}.`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    let guestOrderDTO: any = null;
    const api = new OrdersApi(AXIOS_CONFIG);
    try {
      const items = (cart?.cartBody?.items || []).map((item: any) => ({
        productVariantId: String(item.productVariantId),
        quantity: typeof item.quantity === 'number' ? item.quantity : 1,
      })).filter((item: any) => item.productVariantId);

      if (!isAuthenticated) {
        // ── Guest checkout ──────────────────────────────────────────────────
        const deliveryMethod = isDelivery === true ? 'shippo' : isDelivery === 'custom' ? 'custom' : 'pickup';
        const isCustomDelivery = isDelivery === 'custom';
        // Trim all address fields before sending to avoid Shippo whitespace failures
        const trimmedAddress: GuestAddress = {
          street1: guestDeliveryAddress.street1.trim(),
          street2: guestDeliveryAddress.street2?.trim() || undefined,
          city: guestDeliveryAddress.city.trim(),
          state: guestDeliveryAddress.state.trim().toUpperCase(),
          zip: guestDeliveryAddress.zip.trim(),
          country: guestDeliveryAddress.country.trim().toUpperCase() || 'US',
          name: guestDeliveryAddress.name.trim(),
          phone: guestDeliveryAddress.phone.replace(/[\s-]/g, '').trim(),
        };
        guestOrderDTO = {
          items,
          paymentMethod,
          orderStatus: 'Pending',
          orderRemindingDates: [],
          firstname: guestInfo.firstname.trim(),
          lastname: guestInfo.lastname.trim(),
          email: guestInfo.email.trim(),
          deliveryMethod,
          ...(appliedCoupon && { discountId: appliedCoupon._id || appliedCoupon.id }),
          ...(paymentMethod === 'Card' && storedPaymentMethodId && { paymentMethodId: storedPaymentMethodId }),
          // Only send deliveryAddress for custom delivery — Shippo address is embedded in rateId
          ...(isCustomDelivery && trimmedAddress.street1 && { deliveryAddress: trimmedAddress }),
        };
        const isDeliveryBoolean = isDelivery !== false;
        const guestResponse = await api.guestCheckout(
          guestOrderDTO,
          isDelivery === true ? (selectedShippingRateId || undefined) : undefined,
          isDeliveryBoolean
        );
        // Store auth token — creates account automatically
        await loginWithSession(guestResponse.data.authToken);
        const isNewUser = guestResponse.data.isNewUser;
        notification.success(
          isNewUser ? 'Account created!' : 'Welcome back!',
          isNewUser
            ? 'Your account has been created! Use your email to sign in next time.'
            : 'Your order has been placed successfully.'
        );
        setIsOrderSuccess(true);
        await clearCart();
        removeCoupon();
        const orderId = guestResponse.data.order?.id || guestResponse.data.order?._id;
        const guestEmail = guestInfo.email.trim();
        router.push(buildPath(`/account/orders/${orderId}?guestEmail=${encodeURIComponent(guestEmail)}`));
        return;
      }

      // ── Authenticated checkout ──────────────────────────────────────────
      let billingAddressId: string | undefined = undefined;
      if (isDelivery) {
        billingAddressId = selectedAddressId || undefined;
      } else if (paymentMethod === 'Card') {
        billingAddressId = userAddresses.length > 0 ? userAddresses[0].id : undefined;
      }
      const orderDTO: any = {
        items,
        paymentMethod,
        orderStatus: 'Pending',
        orderRemindingDates: [],
        shippingAddress: data.shipping,
        billingAddress: sameAsShipping ? data.shipping : data.billing,
        ...(appliedCoupon && { discountId: appliedCoupon._id || appliedCoupon.id }),
        ...(isDelivery === 'custom' && selectedAddressId && {
          deliveryMethod: 'custom',
          userAddressId: selectedAddressId,
        }),
        ...(paymentMethod === 'Card' && storedPaymentMethodId && { paymentMethodId: storedPaymentMethodId }),
      };

      const isDeliveryBoolean = isDelivery !== false;
      const response = await api.createCheckout(
        orderDTO,
        isDeliveryBoolean,
        undefined,
        isDeliveryBoolean && isDelivery === true ? (selectedShippingRateId || undefined) : undefined,
        billingAddressId
      );
      if (response?.data?.payment && response.data.payment.hostedInvoiceUrl === 'INSUFFICIENT_CREDIT') {
        setError('You have insufficient credit to complete this order.');
        notification.error('Insufficient credit', 'You do not have enough credit to complete this order.');
        return;
      }
      if (paymentMethod === 'Card') {
        if (storedPaymentMethodId) {
          notification.success('Order placed', 'Your order has been placed and your card has been charged.');
        } else {
          const paymentUrl = response?.data?.payment?.hostedInvoiceUrl;
          if (paymentUrl && paymentUrl !== 'INSUFFICIENT_CREDIT') {
            window.open(paymentUrl, '_blank');
          }
          notification.success('Order placed', 'Your order has been placed successfully.');
        }
        setIsOrderSuccess(true);
        await clearCart();
        removeCoupon();
        router.push(buildPath(`/account/orders/${response.data?.id}`));
        return;
      } else {
        notification.success('Order placed', 'Your order has been placed successfully.');
        setIsOrderSuccess(true);
        await clearCart();
        removeCoupon();
        router.push(buildPath(`/account/orders/${response.data?.id}`));
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      const rawMsg: string = err?.response?.data?.message || err?.message || 'Failed to place order';
      const statusCode: number = err?.response?.status || err?.response?.data?.statusCode || 0;

      // Expired Shippo rate — auto-refetch and retry once for guests
      if (
        !isAuthenticated &&
        isDelivery === true &&
        statusCode === 400 &&
        rawMsg.toLowerCase().includes('shipping rate has expired')
      ) {
        const newRateId = await fetchGuestRates();
        if (newRateId) {
          try {
            const retryResponse = await api.guestCheckout(
              guestOrderDTO,
              newRateId,
              true
            );
            await loginWithSession(retryResponse.data.authToken);
            const isNewUser = retryResponse.data.isNewUser;
            notification.success(
              isNewUser ? 'Account created!' : 'Welcome back!',
              isNewUser
                ? 'Your account has been created! Use your email to sign in next time.'
                : 'Your order has been placed successfully.'
            );
            setIsOrderSuccess(true);
            await clearCart();
            removeCoupon();
            const orderId = retryResponse.data.order?.id || retryResponse.data.order?._id;
            const guestEmailVal = guestInfo.email.trim();
            router.push(buildPath(`/account/orders/${orderId}?guestEmail=${encodeURIComponent(guestEmailVal)}`));
            return;
          } catch (retryErr: any) {
            const retryMsg: string = retryErr?.response?.data?.message || retryErr?.message || 'Failed to place order';
            setError(retryMsg);
            notification.error('Could not place order', retryMsg);
            setIsSubmitting(false);
            return;
          }
        }
        setError('Your shipping rate expired and we could not fetch a new one. Please try again.');
        notification.error('Shipping rate expired', 'Please go back and select a new shipping rate.');
        setIsSubmitting(false);
        return;
      }

      // Out-of-range custom delivery address
      if (statusCode === 400 && isCustomDelivery && (rawMsg.toLowerCase().includes('delivery is not available') || rawMsg.toLowerCase().includes('maximum delivery distance'))) {
        const customDeliveryError = getCustomDeliveryErrorMessage(err);
        setCustomDeliveryAddressError(customDeliveryError);
        if (isAuthenticated) {
          setActiveShippingSection('address');
        } else {
          setGuestAddressError(customDeliveryError);
          setGuestAddressValidated(false);
        }
        setCurrentStep('shipping');
        setIsSubmitting(false);
        return;
      }

      // Address validation failure — scroll back to shipping step, show inline error
      if (statusCode === 400 && rawMsg.toLowerCase().includes('address validation')) {
        setGuestAddressError(rawMsg);
        setCurrentStep('shipping');
        notification.error('Address not found', "We couldn't verify this address. Please double-check the details.");
        setIsSubmitting(false);
        return;
      }

      let msg = rawMsg;
      let toastTitle = 'Could not place order';
      if (statusCode === 403 && msg.toLowerCase().includes('inactive')) {
        toastTitle = 'Account inactive';
        // Older API builds only return a bare "User is inactive" — swap in copy the
        // customer can act on. Newer ones include the pharmacy's contact details.
        if (!msg.toLowerCase().includes('contact')) {
          msg = 'Your account is currently inactive. Please contact the pharmacy to complete your order.';
        }
      } else if (msg.toLowerCase().includes('insufficient balance')) {
        msg = 'Insufficient balance in your account to complete this order.';
      } else if (msg.toLowerCase().includes('browser was not found')) {
        msg = 'A temporary server error occurred while processing your invoice. Please contact support.';
      } else if (statusCode === 400 && msg.toLowerCase().includes('out of stock')) {
        msg = 'One or more items in your cart are out of stock. Please update your cart and try again.';
      } else if (statusCode === 400 && msg.toLowerCase().includes('discount')) {
        msg = 'Your discount code is no longer valid. It has been removed from your order.';
      } else if (statusCode === 400 && msg.toLowerCase().includes('variant')) {
        msg = 'One or more items in your cart are no longer available. Please update your cart and try again.';
      } else if (statusCode >= 500 || !statusCode) {
        msg = 'A server error occurred. Please try again in a moment — your cart has been preserved.';
      }
      setError(msg);
      notification.error(toastTitle, msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if cart is empty - use useEffect to avoid rendering during render
  React.useEffect(() => {
    if (!isOrderSuccess && (!cart || cart?.cartBody?.items?.length === 0 || !cart?.cartBody?.items)) {
      router.push(buildPath('/shop'));
    }
  }, [cart, router, isOrderSuccess]);

  if (!cart || cart?.cartBody?.items?.length === 0 || !cart?.cartBody?.items) {
    return null;
  }

  const subtotal = cart.subTotal ?? Math.round(cart.cartBody.items.reduce((total, item) => total + item.productVariantData.finalPrice * item.quantity, 0) * 100) / 100;
  const discountAmount = (cart.discountedAmount && cart.discountedAmount > 0)
    ? cart.discountedAmount
    : (appliedCoupon ? calculateDiscountForCartItems(cart.cartBody.items, appliedCoupon) : 0);
  const tax = cart.tax ?? 0;
  const total = cart.total ?? (subtotal - discountAmount + (cart.shipping ?? shippingPrice) + tax);

  return (
    <div className="min-h-screen bg-white pb-16 overflow-x-hidden">


      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pt-12 container mx-auto grid gap-10 px-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8 order-2 lg:order-1"
          >
            {/* Header */}
            <div className="mb-8">
              <h1 className="font-semibold text-[#2B4B7C] mb-2 text-3xl">
                Checkout
              </h1>
              <p className="text-[14px] text-[#676c80] text-md leading-relaxed">
                Complete your order information below
              </p>
            </div>

            {/* Guest Info Card — shown when not logged in */}
            {!isAuthenticated && (
              <div className={`bg-white border-2 rounded-[24px] p-6 sm:p-8 mb-4 transition-colors ${guestInfoError ? 'border-red-300' : 'border-gray-100'}`}>
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-hprimary-50 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-hprimary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#2B4B7C] text-lg leading-tight">Guest Checkout</h2>
                    <p className="text-sm text-gray-500 mt-0.5">We&apos;ll create a free account for you so you can track your order.</p>
                  </div>
                </div>
                {guestInfoError && (
                  <div className="flex items-center gap-2 mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {guestInfoError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name <span className="text-red-400">*</span></label>
                    <Input
                      placeholder="Alex"
                      value={guestInfo.firstname}
                      onChange={(e) => { setGuestInfo(p => ({ ...p, firstname: e.target.value })); setGuestInfoError(null); }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name <span className="text-red-400">*</span></label>
                    <Input
                      placeholder="Morgan"
                      value={guestInfo.lastname}
                      onChange={(e) => { setGuestInfo(p => ({ ...p, lastname: e.target.value })); setGuestInfoError(null); }}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={guestInfo.email}
                      onChange={(e) => { setGuestInfo(p => ({ ...p, email: e.target.value })); setGuestInfoError(null); }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Progress Stepper */}
            <div className="bg-white border-2 border-gray-100 rounded-[24px] p-6 mb-8">
              <CheckoutStepper
                currentStep={currentStep}
                onStepClick={handleStepClick}
                completedSteps={completedSteps}
              />
            </div>

            <AnimatePresence mode="wait">
              {/* SHIPPING STEP */}
              {currentStep === 'shipping' && (
                <motion.div
                  ref={shippingRef}
                  key="shipping-step"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  {/* Stock Issues — inline interactive section */}
                  {stockIssues.length > 0 && (
                    <div className="border-2 border-red-300 bg-red-50 rounded-[24px] overflow-hidden">
                      {/* Header */}
                      <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-red-200">
                        <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-red-800 text-base leading-tight">
                            {stockIssues.length === 1
                              ? '1 item needs attention'
                              : `${stockIssues.length} items need attention`}
                          </p>
                          <p className="text-red-600 text-sm mt-0.5">
                            Decrease the quantity or remove items below to continue.
                          </p>
                          <button
                            type="button"
                            onClick={() => setCartOpen(true)}
                            className="text-sm font-semibold text-red-700 underline hover:text-red-800 mt-2"
                          >
                            Go to cart to fix these items
                          </button>
                        </div>
                      </div>

                      {/* Item rows */}
                      <div className="px-6 py-4 space-y-3">
                        {stockIssues.map(item => {
                          const v = item.productVariantData;
                          const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
                          const isOutOfStock = maxQty === 0 || v.inventoryStatus === ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK;

                          return (
                            <div
                              key={item.productVariantId}
                              className="flex flex-col gap-2 bg-white border border-red-200 rounded-2xl p-3"
                            >
                              {/* Top row: thumbnail + name */}
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-50 shrink-0 border border-red-100">
                                  <Image
                                    src={v.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                                    alt={v.name}
                                    width={48}
                                    height={48}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">{v.name}</p>
                                  <p className="text-xs font-medium text-red-600 mt-0.5">
                                    {isOutOfStock
                                      ? 'Out of stock'
                                      : `Only ${maxQty} available — you have ${item.quantity}`}
                                  </p>
                                </div>
                              </div>

                              {/* Bottom row: action buttons */}
                              <div className="flex items-center justify-end gap-2">
                                {isOutOfStock ? (
                                  <button
                                    type="button"
                                    onClick={() => removeFromCart(item.productVariantId)}
                                    disabled={cartIsLoading}
                                    className="text-xs font-semibold text-red-600 border border-red-200 bg-white rounded-full px-3 py-1.5 hover:bg-red-50 transition-colors disabled:opacity-50"
                                  >
                                    Remove
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => updateQuantity(item.productVariantId, maxQty)}
                                      disabled={cartIsLoading || item.quantity === maxQty}
                                      className="text-xs font-semibold text-amber-600 border border-amber-200 bg-amber-50 rounded-full px-2.5 py-1.5 hover:bg-amber-100 transition-colors disabled:opacity-50"
                                    >
                                      Reduce to {maxQty}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeFromCart(item.productVariantId)}
                                      disabled={cartIsLoading}
                                      className="text-xs font-semibold text-red-500 border border-red-200 bg-white rounded-full px-2.5 py-1.5 hover:bg-red-50 transition-colors disabled:opacity-50"
                                    >
                                      Remove
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Delivery Method */}
                  {activeShippingSection === 'method' ? (
                    <div className={`bg-white border-2 rounded-[24px] p-6 sm:p-8 transition-colors duration-200 ${hasAttemptedShippingContinue && isDelivery === null ? 'border-red-300' : 'border-gray-100'}`}>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${hasAttemptedShippingContinue && isDelivery === null ? 'bg-red-100' : 'bg-hprimary-50'}`}>
                            <Truck className={`w-5 h-5 ${hasAttemptedShippingContinue && isDelivery === null ? 'text-red-500' : 'text-hprimary-600'}`} />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-[#2B4B7C]">How would you like to receive your order?</h2>
                            <p className="text-sm text-gray-500">Select your preferred delivery method</p>
                          </div>
                        </div>
                        {hasAttemptedShippingContinue && isDelivery === null && (
                          <span className="shrink-0 text-xs font-semibold text-red-500 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                            Required
                          </span>
                        )}
                      </div>

                      <div className="space-y-2">
                        {deliveryOptions.map((option) => {
                          const active = isDelivery === option.value;
                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setIsDelivery(option.value);
                                setHasAttemptedShippingContinue(false);
                                setSelectedShippingRateId(null);
                                setCustomDeliveryAddressError(null);
                                setShippingPrice(0);
                                // Auto-advance to next section
                                if (option.value === false) {
                                  setActiveShippingSection(null);
                                } else if (selectedAddressId) {
                                  setActiveShippingSection(option.value === true ? 'rate' : null);
                                } else {
                                  setActiveShippingSection('address');
                                }
                              }}
                              className={`relative flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all duration-200 ${active
                                ? 'border-hprimary-400 bg-hprimary-50/80 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-hprimary-200 hover:bg-gray-50/50'
                                }`}
                            >
                              <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${active ? 'border-hprimary-500 bg-hprimary-500' : 'border-gray-300'}`}>
                                {active && <div className="w-2 h-2 rounded-full bg-white" />}
                              </div>
                              <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${active ? 'bg-hprimary-100 text-hprimary-600' : 'bg-gray-100 text-gray-500'}`}>
                                {option.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm ${active ? 'text-hprimary-700' : 'text-gray-900'}`}>{option.label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{option.desc}</p>
                              </div>
                              {option.value === 'custom' && capabilities?.customDeliveryZones && capabilities.customDeliveryZones.length > 0 && (
                                <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${active ? 'bg-hprimary-100 text-hprimary-700' : 'bg-gray-100 text-gray-600'}`}>
                                  From {formatPrice(Math.min(...capabilities.customDeliveryZones.map(z => z.price)))}
                                </span>
                              )}
                              {option.value === false && (
                                <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                  Free
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : isDelivery !== null ? (
                    <button
                      type="button"
                      onClick={() => setActiveShippingSection('method')}
                      className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl hover:border-hprimary-200 hover:bg-hprimary-50/30 transition-all duration-200 text-left"
                    >
                      <div className="w-9 h-9 rounded-xl bg-hprimary-100 flex items-center justify-center shrink-0">
                        {isDelivery === false ? <MapPin className="w-4 h-4 text-hprimary-600" /> : <Truck className="w-4 h-4 text-hprimary-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Delivery Method</p>
                        <p className="text-sm font-semibold text-gray-800 mt-0.5">
                          {isDelivery === false ? 'Store Pickup' : isDelivery === 'custom' ? (capabilities?.customDeliveryTitle || 'Store Delivery') : SHIPPO_DELIVERY_NAME}
                        </p>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-400">Edit</span>
                      </div>
                    </button>
                  ) : null}

                  {/* Guest inline address form — shown instead of address picker for unauthenticated users */}
                  {!isAuthenticated && isDelivery !== null && isDelivery !== false && (
                    guestAddressValidated ? (
                      /* Collapsed summary — click Edit to re-open the form */
                      <button
                        type="button"
                        onClick={() => { setGuestAddressValidated(false); clearGuestRates(); }}
                        className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl hover:border-hprimary-200 hover:bg-hprimary-50/30 transition-all duration-200 text-left"
                      >
                        <div className="w-9 h-9 rounded-xl bg-hprimary-100 flex items-center justify-center shrink-0">
                          <MapPin className="w-4 h-4 text-hprimary-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Delivery Address</p>
                          <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">
                            {guestDeliveryAddress.street1}, {guestDeliveryAddress.city}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                          <span className="text-xs font-medium text-gray-400">Edit</span>
                        </div>
                      </button>
                    ) : (
                      <section className={`bg-white border-2 rounded-[24px] p-6 sm:p-8 transition-colors duration-200 ${(hasAttemptedShippingContinue && !guestDeliveryAddress.street1) || guestAddressError ? 'border-red-300' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-3 mb-6">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${(hasAttemptedShippingContinue && !guestDeliveryAddress.street1) || guestAddressError ? 'bg-red-100' : 'bg-hprimary-50'}`}>
                            <MapPin className={`w-5 h-5 ${(hasAttemptedShippingContinue && !guestDeliveryAddress.street1) || guestAddressError ? 'text-red-500' : 'text-hprimary-600'}`} />
                          </div>
                          <div>
                            <h2 className="font-semibold text-[#2B4B7C] text-lg">Delivery Address</h2>
                            <p className="text-sm text-gray-500">Where should we deliver your order?</p>
                          </div>
                        </div>

                        {/* Inline address validation error */}
                        {guestAddressError && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                              <p className="text-sm font-medium text-red-700">{guestAddressError}</p>
                            </div>
                            {isDelivery === 'custom' && showShippoDelivery && (
                              <button
                                type="button"
                                onClick={handleUseShipping}
                                className="shrink-0 self-start sm:self-auto rounded-full bg-hprimary px-4 py-2 text-sm font-semibold text-white hover:bg-hprimary/90 transition-colors"
                              >
                                Use Shipping
                              </button>
                            )}
                          </div>
                        )}


                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-400">*</span></label>
                              <Input placeholder="John Doe" value={guestDeliveryAddress.name} onChange={(e) => { setGuestDeliveryAddress(p => ({ ...p, name: e.target.value })); setCustomDeliveryAddressError(null); }} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone <span className="text-red-400">*</span></label>
                              <Input type="tel" placeholder="+1 555 000-0000" value={guestDeliveryAddress.phone} onChange={(e) => { setGuestDeliveryAddress(p => ({ ...p, phone: formatPhoneNumber(e.target.value) })); setCustomDeliveryAddressError(null); }} />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Street Address <span className="text-red-400">*</span></label>
                            <Input placeholder="123 Main St" value={guestDeliveryAddress.street1} onChange={(e) => { setGuestDeliveryAddress(p => ({ ...p, street1: e.target.value })); setGuestAddressError(null); setCustomDeliveryAddressError(null); clearGuestRates(); }} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Apt, suite, floor (optional)</label>
                            <Input placeholder="Apt 4B" value={guestDeliveryAddress.street2 || ''} onChange={(e) => { setGuestDeliveryAddress(p => ({ ...p, street2: e.target.value })); setCustomDeliveryAddressError(null); clearGuestRates(); }} />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">City <span className="text-red-400">*</span></label>
                              <Input placeholder="New York" value={guestDeliveryAddress.city} onChange={(e) => { setGuestDeliveryAddress(p => ({ ...p, city: e.target.value })); setGuestAddressError(null); setCustomDeliveryAddressError(null); clearGuestRates(); }} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">State <span className="text-red-400">*</span></label>
                              <select
                                value={guestDeliveryAddress.state}
                                onChange={(e) => { setGuestDeliveryAddress(p => ({ ...p, state: e.target.value })); setGuestAddressError(null); setCustomDeliveryAddressError(null); clearGuestRates(); }}
                                className="w-full h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-hprimary-300 focus:border-hprimary-400"
                              >
                                <option value="">Select state</option>
                                {US_STATES.map(s => (
                                  <option key={s.abbr} value={s.abbr}>{s.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">ZIP Code <span className="text-red-400">*</span></label>
                              <Input
                                placeholder="10001"
                                value={guestDeliveryAddress.zip}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                                  setGuestDeliveryAddress(p => ({ ...p, zip: digits }));
                                  setGuestAddressError(null);
                                  setCustomDeliveryAddressError(null);
                                  clearGuestRates();
                                }}
                                inputMode="numeric"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                              <Input value={guestDeliveryAddress.country} disabled className="bg-gray-50 text-gray-500 cursor-not-allowed" />
                            </div>
                          </div>
                        </div>
                        <p className="mt-4 text-xs text-gray-400">Your address will be saved to your account for future orders.</p>

                        {/* Confirm / Get Rates button */}
                        <button
                          type="button"
                          onClick={validateAndProceed}
                          disabled={guestAddressValidating || shippingRatesLoading || !guestDeliveryAddress.street1 || !guestDeliveryAddress.city || !guestDeliveryAddress.state || !guestDeliveryAddress.zip || !guestDeliveryAddress.name || !guestDeliveryAddress.phone}
                          className="mt-4 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-hprimary text-white font-medium text-sm hover:bg-hprimary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {guestAddressValidating ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Confirming address...
                            </>
                          ) : shippingRatesLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Getting rates...
                            </>
                          ) : isDelivery === true ? (
                            <>
                              <Truck className="w-4 h-4" />
                              {guestAddressValidated ? 'Refresh Rates' : 'Get Shipping Rates'}
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              Confirm Address
                            </>
                          )}
                        </button>
                      </section>
                    )
                  )}

                  {/* Address Section - only for delivery (authenticated users) */}
                  {isAuthenticated && isDelivery !== null && isDelivery !== false && (
                    activeShippingSection !== 'address' && selectedAddressId && !customDeliveryAddressError ? (
                      <button
                        type="button"
                        onClick={() => setActiveShippingSection('address')}
                        className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl hover:border-hprimary-200 hover:bg-hprimary-50/30 transition-all duration-200 text-left"
                      >
                        <div className="w-9 h-9 rounded-xl bg-hprimary-100 flex items-center justify-center shrink-0">
                          <MapPin className="w-4 h-4 text-hprimary-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Delivery Address</p>
                          <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">
                            {(() => { const a = userAddresses.find(x => x.id === selectedAddressId); return a ? `${a.street1}, ${a.city}` : ''; })()}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                          <span className="text-xs font-medium text-gray-400">Edit</span>
                        </div>
                      </button>
                    ) : (
                      <section className={`bg-white border-2 rounded-[24px] p-6 sm:p-8 transition-colors duration-200 ${hasAttemptedShippingContinue && !selectedAddressId ? 'border-red-300' : 'border-gray-100'}`}>
                        <div className="flex items-center justify-between gap-4 mb-6">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${hasAttemptedShippingContinue && !selectedAddressId ? 'bg-red-100' : 'bg-hprimary-50'}`}>
                              <MapPin className={`w-5 h-5 ${hasAttemptedShippingContinue && !selectedAddressId ? 'text-red-500' : 'text-hprimary-600'}`} />
                            </div>
                            <div>
                              <h2 className="font-semibold text-[#2B4B7C] text-lg">Delivery Address</h2>
                              <p className="text-sm text-slate-500">Temperature-aware packaging on every shipment</p>
                            </div>
                          </div>
                          {hasAttemptedShippingContinue && !selectedAddressId && (
                            <span className="shrink-0 text-xs font-semibold text-red-500 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                              Required
                            </span>
                          )}
                        </div>

                        {customDeliveryAddressError && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                              <p className="text-sm font-medium text-red-700">{customDeliveryAddressError}</p>
                            </div>
                            {showShippoDelivery && (
                              <button
                                type="button"
                                onClick={handleUseShipping}
                                className="shrink-0 self-start sm:self-auto rounded-full bg-hprimary px-4 py-2 text-sm font-semibold text-white hover:bg-hprimary/90 transition-colors"
                              >
                                Use Shipping
                              </button>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="md:col-span-2 space-y-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <label className="block font-semibold">
                                Select Address
                              </label>

                              <Button
                                type="button"
                                variant="outline-solid"
                                size="sm"
                                onClick={() => { setEditingAddress(null); setIsAddressModalOpen(true); }}
                                className="w-full sm:w-auto justify-center sm:justify-start"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add New Address
                              </Button>
                            </div>


                            {userAddresses.length > 0 ? (
                              <div className="grid gap-4">
                                {userAddresses.map(addr => (
                                  <div
                                    key={addr.id}
                                    onClick={() => handleAddressSelect(addr)}
                                    className={`group relative flex items-start gap-3 p-4 rounded-2xl border ${selectedAddressId === addr.id
                                      ? 'border-hprimary-500 bg-hprimary-50 shadow-xs'
                                      : 'border-slate-200 bg-white'
                                      } cursor-pointer hover:border-hprimary-300 transition-colors`}
                                  >
                                    <input
                                      type="radio"
                                      name="selectedAddress"
                                      value={addr.id}
                                      checked={selectedAddressId === addr.id}
                                      onChange={() => handleAddressSelect(addr)}
                                      className="mt-1"
                                    />
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-x-2">
                                          <p className="font-semibold text-slate-900">{addr.name}</p>
                                          {addr.isDefault && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                                              Default
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                          {/* Desktop Buttons */}
                                          <div className="hidden sm:flex items-center gap-x-2">
                                            <button
                                              type="button"
                                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingAddress(addr); setIsAddressModalOpen(true); }}
                                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-600 hover:border-hprimary-300 hover:text-hprimary-600"
                                            >
                                              <Edit3 className="h-3 w-3" />
                                            </button>
                                            {!addr.isDefault && (
                                              <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); markAsDefault(addr.id); }}
                                                className="inline-flex items-center gap-1 rounded-full border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-600 hover:border-amber-300 hover:text-amber-700"
                                              >
                                                <Star className="h-3 w-3" />
                                                Make default
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setAddressToDelete(addr.id);
                                                setIsConfirmModalOpen(true);
                                              }}
                                              disabled={deletingId === addr.id}
                                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-600 hover:border-red-300 hover:text-red-700 disabled:opacity-50"
                                            >
                                              {deletingId === addr.id ? (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                              ) : (
                                                <Trash2 className="h-3 w-3" />
                                              )}
                                            </button>
                                          </div>

                                          {/* Mobile Menu */}
                                          <div className="sm:hidden relative" ref={activeMenuId === addr.id ? menuRef : null}>
                                            <button
                                              type="button"
                                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveMenuId(activeMenuId === addr.id ? null : addr.id); }}
                                              className="p-1 rounded-full hover:bg-slate-100 transition-colors"
                                            >
                                              <MoreVertical className="h-5 w-5 text-hmuted" />
                                            </button>

                                            <AnimatePresence>
                                              {activeMenuId === addr.id && (
                                                <motion.div
                                                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                                  transition={{ duration: 0.15 }}
                                                  className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-slate-200 bg-white shadow-lg z-50 origin-top-right py-1"
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      setEditingAddress(addr);
                                                      setIsAddressModalOpen(true);
                                                      setActiveMenuId(null);
                                                    }}
                                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                                  >
                                                    <Edit3 className="h-4 w-4" />
                                                    Edit
                                                  </button>
                                                  {!addr.isDefault && (
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        markAsDefault(addr.id);
                                                        setActiveMenuId(null);
                                                      }}
                                                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-slate-50"
                                                    >
                                                      <Star className="h-4 w-4" />
                                                      Make default
                                                    </button>
                                                  )}
                                                  <div className="my-1 border-t border-slate-100" />
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      setAddressToDelete(addr.id);
                                                      setIsConfirmModalOpen(true);
                                                      setActiveMenuId(null);
                                                    }}
                                                    disabled={deletingId === addr.id}
                                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                                                  >
                                                    {deletingId === addr.id ? (
                                                      <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                      <Trash2 className="h-4 w-4" />
                                                    )}
                                                    Delete
                                                  </button>
                                                </motion.div>
                                              )}
                                            </AnimatePresence>
                                          </div>
                                        </div>
                                      </div>
                                      <p className="text-sm text-slate-600 mt-1">{addr.street1}</p>
                                      {addr.street2 && (
                                        <p className="text-sm text-slate-600">{addr.street2}</p>
                                      )}
                                      <p className="text-sm text-slate-600">
                                        {addr.city}, {addr.state} {addr.zip}
                                      </p>
                                      <p className="text-sm text-slate-600">{addr.country}</p>
                                      {addr.phone && (
                                        <p className="text-sm text-slate-600 mt-1">{addr.phone}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 bg-slate-50 rounded-lg">
                                <MapPin className="h-12 w-12 mx-auto text-slate-400" />
                                <p className="mt-2 text-slate-600">No addresses found</p>
                                <p className="text-sm text-slate-500">Add a new address to continue</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </section>
                    )
                  )}

                  {/* Pickup Info */}
                  {isDelivery === false && storeAddresses.length > 0 && (
                    <section className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-[#2B4B7C]">Pickup Location</h2>
                          <p className="text-sm text-gray-500">Your order will be ready for collection here</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {storeAddresses.map(addr => (
                          <div key={addr.id} className="flex items-start gap-4 bg-green-50/50 border border-green-100 rounded-2xl p-4">
                            <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                              <MapPin className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{addr.name}</p>
                              <p className="text-sm text-gray-600 mt-0.5">{addr.street1}</p>
                              <p className="text-sm text-gray-600">{addr.city}, {addr.state} {addr.zip}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Shipping Options — for Shippo delivery (authenticated: requires selected address; guest: appears after "Get Rates") */}
                  {isDelivery === true && (isAuthenticated ? !!selectedAddressId : shippingRates.length > 0 || shippingRatesLoading || !!shippingRatesError) && (
                    activeShippingSection !== 'rate' && selectedShippingRateId ? (
                      <button
                        type="button"
                        onClick={() => setActiveShippingSection('rate')}
                        className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl hover:border-hprimary-200 hover:bg-hprimary-50/30 transition-all duration-200 text-left"
                      >
                        <div className="w-9 h-9 rounded-xl bg-hprimary-100 flex items-center justify-center shrink-0">
                          <Truck className="w-4 h-4 text-hprimary-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Shipping Method</p>
                          <p className="text-sm font-semibold text-gray-800 mt-0.5">
                            {tieredShippingRates.find(r => r.objectId === selectedShippingRateId)?.tierConfig?.label ?? 'Standard'} · {formatPrice(shippingPrice)}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                          <span className="text-xs font-medium text-gray-400">Edit</span>
                        </div>
                      </button>
                    ) : (
                      <div className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-10 h-10 rounded-xl bg-hprimary-50 flex items-center justify-center shrink-0">
                            <Truck className="w-5 h-5 text-hprimary-600" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-[#2B4B7C]">Shipping Method</h2>
                            <p className="text-sm text-gray-500">Choose your preferred delivery speed</p>
                          </div>
                        </div>

                        {shippingRatesLoading ? (
                          <div className="space-y-3">
                            {[1, 2, 3].map(i => (
                              <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />
                            ))}
                          </div>
                        ) : shippingRatesError ? (
                          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-red-700">Shipping unavailable</p>
                              <p className="text-sm text-red-600 mt-0.5">{shippingRatesError}</p>
                            </div>
                          </div>
                        ) : shippingRates && shippingRates.length > 0 ? (
                          <div className="space-y-3">
                            {tieredShippingRates.map((rate) => {
                              const isSelected = !!selectedShippingRateId && selectedShippingRateId === rate.objectId;
                              return (
                                <button
                                  key={rate.objectId}
                                  type="button"
                                  onClick={() => {
                                    setSelectedShippingRateId(rate.objectId);
                                    setShippingPrice(parseFloat(rate.amount));
                                  }}
                                  className={`relative w-full flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all duration-200 ${isSelected
                                    ? 'border-hprimary-400 bg-hprimary-50/80 shadow-sm'
                                    : 'border-gray-200 bg-white hover:border-hprimary-200 hover:bg-gray-50/50'
                                    }`}
                                >
                                  {/* Radio indicator */}
                                  <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-hprimary-500 bg-hprimary-500' : 'border-gray-300'}`}>
                                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                  </div>
                                  {/* Carrier logo */}
                                  <div className="shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-white border border-gray-200 flex items-center justify-center p-1">
                                    <Image
                                      src={rate.providerImage75 || PLACEHOLDER_IMAGE_SRC}
                                      alt={rate.provider}
                                      width={40}
                                      height={40}
                                      className="w-full h-full object-contain"
                                      onError={(e: any) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                  {/* Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${rate.tierConfig.bg} ${rate.tierConfig.text}`}>
                                        {rate.tierConfig.label}
                                      </span>
                                      {rate.test && (
                                        <span className="text-xs font-normal text-hprimary-600 bg-hprimary-50 border border-hprimary-100 px-1.5 py-0.5 rounded">Test</span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {rate.estimatedDays
                                        ? `Est. ${rate.estimatedDays} day${rate.estimatedDays !== 1 ? 's' : ''}`
                                        : rate.durationTerms || 'Delivery time varies'}
                                      {' · '}{rate.provider} {rate.servicelevel?.name}
                                    </p>
                                  </div>
                                  {/* Price */}
                                  <p className={`shrink-0 text-base font-bold ${isSelected ? 'text-hprimary-700' : 'text-gray-900'}`}>
                                    {formatPrice(parseFloat(rate.amount))}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-10 text-center">
                            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                              <Truck className="w-7 h-7 text-gray-400" />
                            </div>
                            <p className="font-medium text-gray-700">No shipping options available</p>
                            <p className="text-sm text-gray-500 mt-1">Please check the shipping address or try a different location.</p>
                          </div>
                        )}
                      </div>
                    )
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col items-end gap-3">
                    {/* Always-visible stock issues alert - shows when there are stock issues */}
                    {stockIssues.length > 0 && (
                      <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        {stockIssues.length === 1
                          ? '1 item is out of stock or exceeds available quantity'
                          : `${stockIssues.length} items are out of stock or exceed available quantity`}
                      </div>
                    )}
                    {hasAttemptedShippingContinue && isDelivery === null && (
                      <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        Please select a delivery method above to continue.
                      </div>
                    )}
                    {hasAttemptedShippingContinue && isDelivery !== null && isDelivery !== false && (
                      isAuthenticated ? !selectedAddressId : !guestDeliveryAddress.street1
                    ) && (
                      <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        {isAuthenticated ? 'Please select or add a delivery address above to continue.' : 'Please fill in your delivery address above to continue.'}
                      </div>
                    )}
                    {hasAttemptedShippingContinue && isDelivery === true && (isAuthenticated ? !!selectedAddressId : shippingRates.length > 0) && !selectedShippingRateId && !shippingRatesLoading && (
                      <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        Please select a shipping rate above to continue.
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        // Check for quantity-exceeds-inventory issues (not out of stock)
                        const quantityIssues = stockIssues.filter(item => {
                          const v = item.productVariantData;
                          const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
                          return maxQty > 0 && v.inventoryStatus !== ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK;
                        });

                        if (quantityIssues.length > 0) {
                          // Show modal for quantity adjustment
                          setQuantityAdjustItems(quantityIssues);
                          setShowQuantityModal(true);
                        } else if (canProceedToPayment) {
                          goToNextStep();
                        } else if (stockIssues.length === 0) {
                          // Surface guest info error first — scroll to top of form
                          if (!isAuthenticated && (!guestInfo.firstname.trim() || !guestInfo.lastname.trim() || !guestInfo.email.trim())) {
                            setGuestInfoError('Please fill in your name and email above to continue.');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            return;
                          }
                          if (!isAuthenticated && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestInfo.email)) {
                            setGuestInfoError('Please enter a valid email address.');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            return;
                          }
                          // Only surface delivery/address errors when stock and guest info are resolved
                          setHasAttemptedShippingContinue(true);
                        }
                        // If out of stock issues exist, the inline section already explains the block
                      }}
                      className={`flex items-center gap-2 px-6 py-3 rounded-full bg-hsecondary text-white font-medium hover:shadow-lg transition-all ${!canProceedToPayment ? 'opacity-50' : ''}`}
                    >
                      Continue to Payment
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Quantity Adjustment Modal */}
              <Modal
                isOpen={showQuantityModal}
                onClose={() => setShowQuantityModal(false)}
                title="Adjust Quantity"
                size="lg"
              >
                <div className="space-y-4">
                  <p className="text-gray-600">
                    The following items exceed the available quantity. Please adjust the quantity to continue:
                  </p>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                    {quantityAdjustItems.map(item => {
                      const v = item.productVariantData;
                      const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;

                      return (
                        <div
                          key={item.productVariantId}
                          className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl p-3"
                        >
                          {/* Thumbnail */}
                          <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                            <Image
                              src={v.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                              alt={v.name}
                              width={56}
                              height={56}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Name and availability */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{v.name}</p>
                            <p className="text-xs text-amber-600 font-medium mt-0.5">
                              Only {maxQty} available
                            </p>
                          </div>

                          {/* Quantity controls - Only Reduce to button, quantity adjustment is in cart */}
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                // Directly reduce to available quantity
                                updateQuantity(item.productVariantId, maxQty);
                              }}
                              disabled={cartIsLoading || item.quantity === maxQty}
                              className="text-xs font-semibold text-amber-600 border border-amber-200 bg-amber-50 rounded-full px-2.5 py-1.5 hover:bg-amber-100 transition-colors disabled:opacity-50"
                            >
                              Reduce to {maxQty}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        // Check if all quantity issues are resolved
                        const remainingIssues = stockIssues.filter(item => {
                          const v = item.productVariantData;
                          const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
                          return maxQty > 0 && v.inventoryStatus !== ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK;
                        });

                        if (remainingIssues.length === 0) {
                          setShowQuantityModal(false);
                          goToNextStep();
                        } else {
                          setQuantityAdjustItems(remainingIssues);
                        }
                      }}
                      className="px-6 py-2.5 rounded-full bg-hsecondary text-white font-medium hover:shadow-lg transition-all"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              </Modal>

              {/* PAYMENT STEP */}
              {currentStep === 'payment' && (
                <motion.div
                  ref={paymentRef}
                  key="payment-step"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  {/* Payment Method */}
                  <div className="bg-white border-2 border-gray-100 rounded-[24px] p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <CreditCard className="w-6 h-6 text-[#2B4B7C]" />
                      <h2 className="text-xl font-semibold text-[#2B4B7C]">Payment Method</h2>
                    </div>

                    <div className="space-y-3">
                      {availablePaymentMethods.map((pm) => {
                        const active = paymentMethod === pm.value;

                        return (
                          <button
                            key={pm.value}
                            type="button"
                            onClick={() => { setPaymentMethod(pm.value); setStoredPaymentMethodId(null); setCardComplete(false); setCardError(null); }}
                            className={`
                            group relative flex w-full items-center justify-between rounded-xl border-2 p-3
                            transition-all duration-200 ease-out
                            focus:outline-hidden
                            ${active
                                ? `${pm.activeClass} shadow-md scale-[1.02]`
                                : `${pm.className} border-gray-200 bg-white hover:shadow-xs hover:-translate-y-0.5`
                              }
                          `}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`
                                flex items-center justify-center rounded-lg p-2
                                transition-colors duration-200
                                ${pm.value === 'Card'
                                    ? ' text-blue-600'
                                    : pm.value === 'Cash'
                                      ? ' text-amber-600'
                                      : ' text-emerald-600'
                                  }
                                ${active ? 'opacity-80' : 'group-hover:opacity-90'}
                              `}
                              >
                                {pm.icon}
                              </div>

                              <span className="text-sm font-semibold text-gray-900">
                                {pm.label}
                              </span>
                            </div>

                            {active && (
                              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-current shrink-0">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <p className="text-xs text-slate-500 mt-4 px-1">
                      {paymentMethod === 'Card' &&
                        'Enter your card details below to pay securely.'}
                      {paymentMethod === 'Cash' &&
                        'Pay with cash at the time of delivery or pickup.'}
                    </p>
                  </div>

                  {/* Stripe Card Element — only shown when Card is selected */}
                  {paymentMethod === 'Card' && stripePromise && (
                    <div className="bg-white border-2 border-gray-100 rounded-[24px] p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                          <Lock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-[#2B4B7C]">Card Details</h2>
                          <p className="text-sm text-gray-500">Your payment info is encrypted and secure</p>
                        </div>
                      </div>
                      <Elements stripe={stripePromise}>
                        <CardSection
                          ref={cardSectionRef}
                          onCardChange={(complete, errorMessage) => {
                            setCardComplete(complete);
                            if (complete) setCardError(null);
                            else if (errorMessage) setCardError(errorMessage);
                          }}
                        />
                      </Elements>
                      {cardError && (
                        <p className="mt-3 text-sm text-red-500 flex items-center gap-1.5">
                          <AlertCircle className="w-4 h-4 shrink-0" />
                          {cardError}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                    <button
                      type="button"
                      onClick={goToPreviousStep}
                      className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full border border-hsecondary-500 text-hsecondary-500 font-medium hover:bg-hsecondary-500 hover:text-white transition-all"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Shipping
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        if (paymentMethod === 'Card') {
                          setIsCreatingPaymentMethod(true);
                          setCardError(null);
                          const result = await cardSectionRef.current?.createPaymentMethod();
                          setIsCreatingPaymentMethod(false);
                          if (!result || 'error' in result) {
                            setCardError((result as any)?.error || 'Please check your card details.');
                            return;
                          }
                          setStoredPaymentMethodId(result.id);
                        }
                        goToNextStep();
                      }}
                      disabled={!canProceedToReview || isCreatingPaymentMethod}
                      className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full bg-hsecondary text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCreatingPaymentMethod ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Validating card...
                        </>
                      ) : (
                        <>
                          Review Order
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>

                </motion.div>
              )}

              {/* REVIEW STEP */}
              {currentStep === 'review' && (
                <motion.div
                  ref={reviewRef}
                  key="review-step"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div className="bg-white border-2 border-gray-100 rounded-[24px] p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <ClipboardCheck className="w-6 h-6 text-[#2B4B7C]" />
                      <h2 className="text-xl font-semibold text-[#2B4B7C]">Review Your Order</h2>
                    </div>

                    <div className="space-y-6">
                      {/* Delivery Summary */}
                      <div className="border-b pb-6">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Delivery Method</h3>
                        <div className="flex items-center gap-3">
                          {isDelivery === true && <Truck className="w-5 h-5 text-hprimary-500" />}
                          {isDelivery === 'custom' && <Truck className="w-5 h-5 text-hprimary-500" />}
                          {isDelivery === false && <MapPin className="w-5 h-5 text-hprimary-500" />}
                          <span className="font-medium text-gray-900">
                            {isDelivery === true && SHIPPO_DELIVERY_NAME}
                            {isDelivery === 'custom' && (capabilities?.customDeliveryTitle || 'Store Delivery')}
                            {isDelivery === false && 'Store Pickup'}
                          </span>
                        </div>
                        {isDelivery && selectedAddressId && userAddresses.find(a => a.id === selectedAddressId) && (
                          <p className="mt-2 text-sm text-gray-600">
                            {userAddresses.find(a => a.id === selectedAddressId)?.street1}, {userAddresses.find(a => a.id === selectedAddressId)?.city}
                          </p>
                        )}
                      </div>

                      {/* Payment Summary */}
                      <div className="border-b pb-6">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment Method</h3>
                        <div className="flex items-center gap-3">
                          {paymentMethod === 'Card' && <CreditCard className="w-5 h-5 text-blue-500" />}
                          {paymentMethod === 'Cash' && <PackageCheck className="w-5 h-5 text-amber-500" />}
                          <span className="font-medium text-gray-900">
                            {paymentMethod === 'Card' && 'Credit/Debit Card'}
                            {paymentMethod === 'Cash' && 'Cash on Delivery/Pickup'}
                          </span>
                        </div>
                      </div>

                      {/* Order Items Preview */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Items ({cart?.cartBody?.items?.length || 0})</h3>
                        <div className="space-y-3">
                          {cart?.cartBody?.items?.slice(0, 3).map((item: any) => (
                            <CheckoutItem key={item.productVariantId || item.id} item={item} />
                          ))}
                          {cart?.cartBody?.items?.length > 3 && (
                            <p className="text-sm text-gray-500 text-center">
                              +{cart.cartBody.items.length - 3} more items
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                    <button
                      type="button"
                      onClick={goToPreviousStep}
                      className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full border border-hsecondary-500 text-hsecondary-500 font-medium hover:bg-hsecondary-500 hover:text-white transition-all"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Payment
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex sm:hidden items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full bg-hsecondary text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="w-4 h-4" />
                      {isSubmitting ? 'Placing order...' : 'Place Secure Order'}
                    </button>
                  </div>

                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
            className="flex flex-col w-full lg:sticky lg:top-24 lg:col-span-1 order-1 lg:order-2"
          >
            {/* Order Summary */}
            <div className="bg-linear-to-br from-hsecondary/10 to-hsecondary/10 rounded-[24px] p-8 border-2 border-hsecondary/20 lg:sticky lg:top-24 w-full">
              <h2 className="font-semibold text-hsecondary mb-6 text-2xl">Order Summary</h2>


              {/* Cart Summary */}
              <section className="mt-8 pt-6 border-t border-slate-100">
                <div className="space-y-4 mb-6 max-h-80 overflow-y-auto pr-1">
                  {cart?.cartBody?.items?.map((item: any) => {
                    const variantId = item.productVariantId || item.productVariantData?.id || item.productVariantData?._id;
                    const productId = item.productVariantData?.productId;
                    const hasRestrictions = appliedCoupon && (
                      (appliedCoupon.productsEligible?.length ?? 0) > 0 ||
                      (appliedCoupon.variantsEligible?.length ?? 0) > 0
                    );
                    const isDiscounted = appliedCoupon && (
                      !hasRestrictions || isProductEligibleForDiscount(productId, variantId, appliedCoupon)
                    );
                    return (
                      <CheckoutItem key={item.productVariantId || item.id} item={item} isDiscounted={!!isDiscounted} />
                    );
                  })}
                </div>

                <div className="h-px bg-hsecondary/20 my-4" />

                {/* Coupon Code Section */}
                <div className="mb-6">
                  <CouponCodeInput
                    userId={user?.id}
                    cartItems={cart?.cartBody?.items ?? []}
                    className="mb-4"
                  />
                </div>

                {/* Totals */}
                <div className="text-sm text-slate-600 space-y-3 py-4">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    {cartIsFetching ? (
                      <div className="h-4 w-16 bg-slate-200 rounded animate-pulse" />
                    ) : (
                      <span className="font-semibold text-slate-900">{formatPrice(subtotal)}</span>
                    )}
                  </div>
                  {appliedCoupon && (
                    <div className={`flex items-center justify-between ${discountAmount > 0 ? 'text-green-600' : 'text-slate-500'}`}>
                      <span>Discount ({appliedCoupon.code})</span>
                      {cartIsFetching ? (
                        <div className="h-4 w-16 bg-current opacity-20 rounded animate-pulse" />
                      ) : (
                        <span className="font-semibold">
                          {discountAmount > 0 ? `-${formatPrice(discountAmount)}` : 'Does not apply to items'}
                        </span>
                      )}
                    </div>
                  )}
                  {isDelivery && (
                    <div className="flex items-center justify-between">
                      <span>{isDelivery === 'custom' ? 'Local Delivery' : 'Shipping'}</span>
                      {cartIsFetching ? (
                        <div className="h-4 w-14 bg-slate-200 rounded animate-pulse" />
                      ) : isDelivery === 'custom' && customDeliveryAddressError ? (
                        <span className="font-semibold text-red-600">Unavailable</span>
                      ) : (
                        <span className="font-semibold">{formatPrice(cart.shipping ?? shippingPrice)}</span>
                      )}
                    </div>
                  )}
                  {tax > 0 && (
                    <div className="flex items-center justify-between">
                      <span>Estimated tax</span>
                      {cartIsFetching ? (
                        <div className="h-4 w-12 bg-slate-200 rounded animate-pulse" />
                      ) : (
                        <span className="font-semibold">{formatPrice(tax)}</span>
                      )}
                    </div>
                  )}
                  <div className="h-px bg-hsecondary/20 mt-6" />

                  <div className="flex items-center justify-between mb-6">
                    <span className="font-semibold text-[16px] text-hsecondary">Total</span>
                    {cartIsFetching ? (
                      <div className="h-7 w-24 bg-hsecondary/20 rounded animate-pulse" />
                    ) : (
                      <span className="font-bold text-[24px] text-hsecondary">{formatPrice(total)}</span>
                    )}
                  </div>
                </div>

                <div className="bg-white/80 rounded-xl p-4">
                  <p className="text-[11px] text-[#676c80] leading-[1.6]">
                    <strong className="text-hsecondary">Payment:</strong>{' '}
                    {isDelivery === true
                      ? 'Shipping Delivery orders require card payment.'
                      : 'We accept cash, credit cards, and all major payment methods.'}
                  </p>
                </div>
              </section>

              {/* Checkout Button */}
              {currentStep === 'review' && error && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="break-words min-w-0">{error}</span>
                  </div>
                </div>
              )}
              {Object.keys(errors).length > 0 && currentStep === 'review' && (
                <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold mb-1">Please fix the following errors:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {errors.shipping?.name && <li>{errors.shipping.name.message}</li>}
                        {errors.shipping?.phone && <li>{errors.shipping.phone.message}</li>}
                        {errors.shipping?.street1 && <li>{errors.shipping.street1.message}</li>}
                        {errors.shipping?.city && <li>{errors.shipping.city.message}</li>}
                        {errors.shipping?.state && <li>{errors.shipping.state.message}</li>}
                        {errors.shipping?.zip && <li>{errors.shipping.zip.message}</li>}
                        {errors.shipping?.country && <li>{errors.shipping.country.message}</li>}
                        {!sameAsShipping && errors.billing?.name && <li>Billing: {errors.billing.name.message}</li>}
                        {!sameAsShipping && errors.billing?.street1 && <li>Billing: {errors.billing.street1.message}</li>}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {currentStep === 'review' && (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="font-medium text-[14px] px-6 py-3 rounded-full text-white hover:bg-hsecondary-500 hover:shadow-lg transition-all duration-300 mt-4 w-full bg-hsecondary hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CreditCard className="h-5 w-5" />
                  {isSubmitting ? 'Placing order...' : 'Place Secure Order'}
                </button>
              )}

              <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <Lock className="h-4 w-4" />
                Fully encrypted checkout — cancel anytime before shipment.
              </p>
            </div>

          </motion.aside>

        </div>
      </form>
      <AddressFormModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        initialAddress={editingAddress}
        onAddressAdded={(addr) => {
          refresh().then(() => {
            setSelectedAddressId(addr.id);
            setCustomDeliveryAddressError(null);
            setValue('shipping.name', addr.name);
            setValue('shipping.phone', addr.phone || '');
            setValue('shipping.street1', addr.street1);
            setValue('shipping.street2', addr.street2 || '');
            setValue('shipping.city', addr.city);
            setValue('shipping.state', addr.state);
            setValue('shipping.zip', addr.zip);
            setValue('shipping.country', addr.country);
          });
        }}
        onAddressUpdated={() => {
          refresh();
        }}
      />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setAddressToDelete(null);
        }}
        onConfirm={async () => {
          if (addressToDelete) {
            try {
              await removeAddress(addressToDelete);
              if (selectedAddressId === addressToDelete) {
                setSelectedAddressId(null);
                setCustomDeliveryAddressError(null);
                hasInitializedAddressRef.current = true;
                setValue('shipping.name', '');
                setValue('shipping.phone', '');
                setValue('shipping.street1', '');
                setValue('shipping.street2', '');
                setValue('shipping.city', '');
                setValue('shipping.state', '');
                setValue('shipping.zip', '');
                setValue('shipping.country', 'United States');
                if (currentStep !== 'shipping') {
                  setCurrentStep('shipping');
                }
                setCompletedSteps(prev => prev.filter(s => s !== 'shipping'));
              }
              notification.success('Address deleted successfully', 'Deleted successfully');
            } catch (e) {
              notification.error('Failed to delete address', 'Failed to delete address');
            }
            setIsConfirmModalOpen(false);
            setAddressToDelete(null);
          }
        }}
        title="Delete Address"
        message="Are you sure you want to delete this address? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
      {/* </div> */}
    </div>
  );
}
